/**
 * Minimal STRUCTURAL shapes for the foreman DTOs, shared by every framework-agnostic
 * foreman util in this library.
 *
 * These exist because `libs/xplat/utils` is tagged `type:util` + `framework:agnostic`
 * while `@ups/xplat/api/dto` is `type:lib` + `framework:angular`, an edge
 * `@nx/enforce-module-boundaries` rejects on both axes. Callers pass the real DTOs
 * unchanged (TypeScript matches these structurally) and a DTO field rename still fails
 * loudly at the call site.
 *
 * Declare a shape here the moment a SECOND util needs it, rather than copying the
 * interface. Two near-identical structural types is the duplication this library is
 * supposed to be removing.
 */

/**
 * The phase/work-order fields the foreman utils read. `ForemanPhase` satisfies it.
 *
 * All three are optional and nullable ON PURPOSE. The Sparta DTO (`ForemanPhaseDto.cs`)
 * declares `PhaseID`, `PhaseName` and `Description` nullable while the TS `ForemanPhase`
 * interface declares them required and non-nullable (#30151). This shape models what the
 * API can actually send, so consumers are forced to handle the null case instead of
 * inheriting an overstated contract.
 */
export interface ForemanPhaseLike {
  foremanPhaseID?: string | null;
  phaseName?: string | null;
  description?: string | null;
  /**
   * Viewpoint's per-phase timesheet block (`udTimesheetBlock` on bJCJP, #30262). VP sends a
   * `char(1)` where `'Y'` means the phase is blocked for new time entry. Optional and nullable
   * because the flag is unset on most phases and older DTOs never carried it. Read it through
   * {@link isWorkOrderTimesheetBlocked} rather than comparing the raw string, so casing and
   * whitespace are normalized in one place.
   */
  udTimesheetBlock?: string | null;
  /**
   * Whether the phase is a billable work order (`ForemanPhase.isBillable`). Optional and
   * nullable because older DTOs and non-foreman work orders may not carry it; the Sparta API
   * derives it from the VP phase (`IsBillable != "Non Billable"`). Read by the Company-15
   * billable default rule (WI 31493) via {@link selectDefaultWorkOrderIdsForCompany}.
   */
  isBillable?: boolean | null;
}
